/**
 * The pure core of the when-clause editor, ported from t3code.
 *
 * A `when` clause is stored as an AST but edited as a flat list of rows: the
 * parser nests `a && b && c` as `and(and(a, b), c)`, while the editor draws
 * three siblings under one operator. Everything here is that translation, kept
 * out of the components so the recursion can be tested without a DOM.
 *
 * The invariant worth stating: a group with fewer than two children is not a
 * group. `buildWhenExpressionGroup` collapses one child back to a bare
 * condition and none to `undefined`, because an `and` node holding a single
 * operand is a shape the encoder cannot serialise and the parser will not
 * accept — it would fail on save rather than on edit.
 *
 * @module
 */
import type { KeybindingWhenNode } from "@cmmd-forge/contracts";
import { parseKeybindingWhenExpression } from "@cmmd-forge/shared/keybindings";

/** The two operators a group can carry. `not` is a modifier, not a group. */
export type BooleanOperator = "and" | "or";

/**
 * The variable a freshly added condition starts on.
 *
 * A real context rather than a placeholder: an empty select cannot be saved,
 * so a new row would arrive already invalid.
 */
const DEFAULT_WHEN_VARIABLE = "terminalFocus";

/** Siblings under one operator, with nesting of that same operator flattened away. */
export function flattenWhenChildren(
  node: KeybindingWhenNode,
  operator: BooleanOperator,
): ReadonlyArray<KeybindingWhenNode> {
  // Stopping at a different operator is what preserves precedence: flattening
  // `a && (b || c)` into three `&&` siblings would be a different expression.
  if (node.type !== operator) return [node];
  return [
    ...flattenWhenChildren(node.left, operator),
    ...flattenWhenChildren(node.right, operator),
  ];
}

/** Rebuild a tree from siblings, collapsing groups that no longer have two. */
export function buildWhenExpressionGroup(
  children: ReadonlyArray<KeybindingWhenNode>,
  operator: BooleanOperator,
): KeybindingWhenNode | undefined {
  const first = children[0];
  if (!first) return undefined;
  return children
    .slice(1)
    .reduce<KeybindingWhenNode>((left, right) => ({ type: operator, left, right }), first);
}

/**
 * Read a node as a single editable condition, or null if it is a group.
 *
 * `!a` counts: it is one row with its Not toggle on. `!(a && b)` does not —
 * that is a negated group, and drawing it as a condition would hide its
 * children.
 */
export function conditionParts(
  node: KeybindingWhenNode,
): { readonly identifier: string; readonly negated: boolean } | null {
  if (node.type === "identifier") return { identifier: node.name, negated: false };
  if (node.type === "not" && node.node.type === "identifier") {
    return { identifier: node.node.name, negated: true };
  }
  return null;
}

/** Swap a condition's variable, preserving whether it is negated. */
export function setConditionIdentifier(
  node: KeybindingWhenNode,
  identifier: string,
): KeybindingWhenNode {
  const parts = conditionParts(node);
  if (!parts) return node;
  const next: KeybindingWhenNode = { type: "identifier", name: identifier };
  return parts.negated ? { type: "not", node: next } : next;
}

/**
 * Turn a condition's negation on or off.
 *
 * Rebuilt from the identifier rather than wrapped, so toggling twice cannot
 * stack `!!` — which parses to something the evaluator treats as a double
 * negation and the user never asked for. A group has no identifier, so there
 * negation wraps the whole node.
 */
export function setConditionNegated(
  node: KeybindingWhenNode,
  negated: boolean,
): KeybindingWhenNode {
  const parts = conditionParts(node);
  if (!parts) return negated ? { type: "not", node } : node;
  const identifier: KeybindingWhenNode = { type: "identifier", name: parts.identifier };
  return negated ? { type: "not", node: identifier } : identifier;
}

/** A new single condition, on a real variable so it is savable immediately. */
export function defaultWhenCondition(): KeybindingWhenNode {
  return { type: "identifier", name: DEFAULT_WHEN_VARIABLE };
}

/**
 * A new group, seeded with two conditions.
 *
 * Two, because one is not a group — a fresh group with a single row would be
 * collapsed away by `buildWhenExpressionGroup` on the very next edit. The
 * second is negated so the pair reads as a contrast rather than a duplicate.
 */
export function defaultWhenGroup(operator: BooleanOperator = "and"): KeybindingWhenNode {
  return {
    type: operator,
    left: defaultWhenCondition(),
    right: { type: "not", node: defaultWhenCondition() },
  };
}

/**
 * A when-clause printed for a person, ported from t3code.
 *
 * Distinct from `encodeWhenAst` in `@cmmd-forge/shared/keybindings`, which
 * parenthesises unconditionally. That is right for its job — it produces the
 * canonical string two rules are compared by, where consistency matters and
 * nobody reads the output — but it renders `!a` as `!(a)` and `a && b` as
 * `(a && b)`, which is what the When column would show.
 *
 * Only groups are wrapped. An identifier and a negation are already
 * unambiguous, so parenthesising them adds noise and no meaning.
 */
export function whenAstToExpression(node: KeybindingWhenNode | undefined): string {
  if (!node) return "";
  switch (node.type) {
    case "identifier":
      return node.name;
    case "not":
      return `!${wrapWhenExpression(node.node)}`;
    case "and":
      return `${wrapWhenExpression(node.left)} && ${wrapWhenExpression(node.right)}`;
    case "or":
      return `${wrapWhenExpression(node.left)} || ${wrapWhenExpression(node.right)}`;
  }
}

function wrapWhenExpression(node: KeybindingWhenNode): string {
  if (node.type === "identifier" || node.type === "not") return whenAstToExpression(node);
  return `(${whenAstToExpression(node)})`;
}

/**
 * Parse what the reader typed, distinguishing "empty" from "wrong".
 *
 * Three outcomes, not two: an empty box is a valid clause meaning
 * unconditional, and collapsing it into the error case would make clearing a
 * when-clause impossible. `undefined` is how the caller drops the clause.
 */
export function parseWhenExpressionDraft(
  expression: string,
):
  | { readonly ok: true; readonly value: KeybindingWhenNode | undefined }
  | { readonly ok: false; readonly message: string } {
  const trimmed = expression.trim();
  if (trimmed.length === 0) return { ok: true, value: undefined };

  const ast = parseKeybindingWhenExpression(trimmed);
  if (!ast) return { ok: false, message: "Use variables with !, &&, ||, and parentheses." };
  return { ok: true, value: ast };
}

/**
 * Identifiers the evaluator will never have a value for.
 *
 * Not an error: `keybindings.json` is hand-editable and a context can be typed
 * before it exists, or survive one being renamed. The clause still parses and
 * still saves — it simply never matches — so this drives a warning rather than
 * a validation failure.
 */
export function unknownWhenVariables(
  node: KeybindingWhenNode | undefined,
  known: ReadonlyArray<string>,
): ReadonlyArray<string> {
  const knownSet = new Set(known);
  return [...collectWhenIdentifiers(node)].filter((name) => !knownSet.has(name)).toSorted();
}

function collectWhenIdentifiers(node: KeybindingWhenNode | undefined): ReadonlySet<string> {
  if (!node) return new Set();
  switch (node.type) {
    case "identifier":
      return new Set([node.name]);
    case "not":
      return collectWhenIdentifiers(node.node);
    case "and":
    case "or":
      return new Set([...collectWhenIdentifiers(node.left), ...collectWhenIdentifiers(node.right)]);
  }
}
